export enum DownloadState {
	preparing = 'Preparing',
	failed = 'Failed',
	downloading = 'Downloading',
	downloaded = 'Downloaded',
}

export interface Download {
	/**
	 * The MIME type as specified in the Content-Type response header from downloading the document.
	 */
	contentType?: string;

	/**
	 * The file name that will ultimately be used when saving the download. Its value will be
	 * assigned from other fields in the following order of precedence, and may change over time
	 * as server-side data is discovered at different points during the process:
	 *
	 * - {@link fileNameFromContentDisposition}
	 * - {@link fileNameFromClient} using the file extension derived from {@link contentType} (if
	 *   not 'application/octet-stream')
	 * - {@link fileNameFromClient} using the file extension from {@link url}
	 * - {@link fileNameFromClient}
	 */
	fileName: string;

	/**
	 * A file name suggestion from the client. {@see fileName}
	 */
	fileNameFromClient: string;

	/**
	 * A file name suggestion discovered from the Content-Disposition response header when
	 * downloading the document. {@see fileName}
	 */
	fileNameFromContentDisposition?: string;

	/**
	 * A unique identifier for this download. `Download` objects are ephemeral, so this field is
	 * used to determine whether two `Download` instances refer to the same download.
	 */
	id: unknown;

	/**
	 * A human friendly name for the download, i.e. how it should be presented in the UI to the
	 * user.
	 */
	label: string;

	/**
	 * A number from 0-100 representing how much of the download is complete. If the total size of
	 * the download cannot be determined, this field will not have a value until the download has
	 * completed.
	 */
	progress?: number;

	/**
	 *
	 */
	state: DownloadState;

	/**
	 * The URL from which the download will be fetched.
	 */
	url?: string;
}

export type DownloadPredicate = (download: Download) => boolean;

export const downloadPredicates: Record<PropertyKey, DownloadPredicate> = {
	isActive(download: Download): boolean {
		return (
			download.state === DownloadState.preparing ||
			download.state === DownloadState.downloading
		);
	},

	isCompleted(download: Download): boolean {
		return !this.isActive(download);
	},

	isFailed(download: Download): boolean {
		return download.state === DownloadState.failed;
	},
};
